import { landmineNames } from '@mm/constants/landmine-names';
import React, { useEffect } from 'react';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import './ZapierPanel.scss';
import { ZapierIcon } from '@mm/icons';
import { BaseIntegration } from '../../../beta/modules/Integrations/BaseIntegration/BaseIntegration';
import { BaseIntegrationHeader } from '../../../beta/modules/Integrations/BaseIntegration/BaseIntegrationHeader';
import { ZapierApiKey } from './ZapierApiKey';
import { ZapierSuppressionLists } from '../../../beta/modules/Integrations/NativeIntegrations/components/ContactSuppressions/ZapierSuppressionLists/ZapierSuppressionLists';
import { BaseIntegrationEmptyState } from '../../../beta/modules/Integrations/BaseIntegration/BaseIntegrationEmptyState';
import { autoIncrementedName } from '../../../../../common/helpers/name-autoincrement';
import { selectZapierSuppressionList } from '@mm/state/modules/suppressionListsTableState/selectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { ReactComponent as ZapierSuppressionIcon } from '../../../beta/modules/Integrations/icons/zapier-suppression.svg';
import { useSelector } from 'react-redux';
import { useSuppressionListsCRUDActions } from '@mm/state/actions/SuppressionListsCRUDActions';
import { CaptureActionEnum } from '@mm/state/model/SuppressionSettingsModel';
import { StoreState } from '@mm/state/store-interfaces';
import { ContactSuppressionHeader } from '../../../beta/modules/Integrations/NativeIntegrations/components/ContactSuppressions/ContactSuppressionHeader';

const ZAPIER_EMBED_PLACEHOLDER_ID = 'zapier-panel__zapier-embed-placeholder';
const ZAPIER_EMBED_SCRIPT_ID = 'zapier-panel__zapier-embed-script';
const ZAPIER_EMBED_URL =
  'https://cdn.zapier.com/packages/partner-sdk/v0/zapier-elements/zapier-elements.esm.js';
const ZAPIER_EMBED_STYLESHEET_URL =
  'https://cdn.zapier.com/packages/partner-sdk/v0/zapier-elements/zapier-elements.css';

const ZapierPanel = () => {
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const zapierSuppressions = useSelector(selectZapierSuppressionList);
  const apiKeys = useSelector((state: StoreState) => state.apiKeys.entities);
  const zapierKeys = apiKeys.filter(k => k.connectionType === 'Zapier');
  const apiKey = zapierKeys.find(k => k.active);
  const { create } = useSuppressionListsCRUDActions();

  const handleZapierSuppressionClick = async () => {
    const UNTITLED_SUPPRESSION_NAME = 'Zapier List';
    await create(activeFbPageId, {
      enabled: true,
      sourceKind: 'zapier',
      sourceId: apiKey?.id,
      name: autoIncrementedName(
        UNTITLED_SUPPRESSION_NAME,
        zapierSuppressions.map(c => c.name),
      ),
      captureAction: CaptureActionEnum.do_not_send,
    });
  };

  // Add Zapier Embed to page
  useEffect(() => {
    const zapierEmbedScript = document.createElement('script');
    zapierEmbedScript.id = ZAPIER_EMBED_SCRIPT_ID;
    zapierEmbedScript.type = 'module';
    zapierEmbedScript.src = ZAPIER_EMBED_URL;
    document.head.appendChild(zapierEmbedScript);

    const stylesheet = document.createElement('link');
    stylesheet.rel = 'stylesheet';
    stylesheet.href = ZAPIER_EMBED_STYLESHEET_URL;
    document.head.appendChild(stylesheet);

    const element: any = document.createElement('zapier-zap-templates');
    element.theme = 'light';
    element.ids =
      '1559118,1563956,1563958,1563959,1563962,1563963,1563964,1563965,1563967,1563972';
    element.limit = 10;
    element.linkTarget = 'new-tab';
    element.presentation = 'row';
    element.useThisZap = 'show';
    const container = document.getElementById(ZAPIER_EMBED_PLACEHOLDER_ID);
    container?.appendChild(element);

    return () => {
      if (zapierEmbedScript && zapierEmbedScript.parentNode) {
        zapierEmbedScript.parentNode.removeChild(zapierEmbedScript);
      }
    };
  }, []);

  const sectionProps = {
    landmineName: landmineNames.INTEGRATIONS_ZAPIER,
    featureFlag: 'zapier',
  };

  const header = (
    <div className="zapier-panel--header-container">
      <PrimaryButton
        className="integrations__header-add-button-secondary"
        onClick={e => {
          e?.stopPropagation();
          window.open(
            'https://zapier.com/apps/mobilemonkey/integrations',
            '_blank',
          );
        }}
      >
        Create your own Zap template
      </PrimaryButton>
    </div>
  );

  return (
    <BaseIntegration
      openOnMount
      titleComponent={
        <BaseIntegrationHeader
          title="Zapier"
          upgradeButtonName="Create your own Zap template"
          subtitle="Choose from our list of pre-built templates to integrate
           with other applications"
          icon={<ZapierIcon />}
          headerRightComponent={header}
          css={{ icon: 'integrations__zapier-header-icon' }}
          {...sectionProps}
        />
      }
      integrationType="zapier"
    >
      <div className="zapier-panel__main-container">
        <span className="zapier-panel__label">Credentials</span>
        <div className="zapier-panel__details-container">
          <ZapierApiKey
            caption="You'll need this key to connect your Zapier account to
              MobileMonkey account"
          />

          <span className="zapier-panel__label">Sources</span>
          <div className="zapier-panel__field-wrapper">
            <ContactSuppressionHeader
              isSuppression
              onClick={handleZapierSuppressionClick}
              count={zapierSuppressions?.length}
            />
            {zapierSuppressions.length ? (
              <ZapierSuppressionLists />
            ) : (
              <BaseIntegrationEmptyState
                button="Add a Zapier List"
                onClick={handleZapierSuppressionClick}
                icon={<ZapierSuppressionIcon />}
                landmineName={landmineNames.INTEGRATIONS_CONTACT_SUPPRESSION}
              />
            )}
          </div>

          <span className="zapier-panel__label">Destinations</span>
          <div
            className="zapier-panel__embed-container"
            id={ZAPIER_EMBED_PLACEHOLDER_ID}
          />
        </div>
      </div>
    </BaseIntegration>
  );
};

export default ZapierPanel;
